import React from 'react';
import { useTranslation } from '../../i18n';
import { AppIcon } from './icons';

interface ConnectionStatusProps {
  status: 'connecting' | 'reconnecting' | 'error';
  reconnectAttempts?: number;
  onGoHome: () => void;
  errorMessage?: string;
}

/**
 * Reusable component for displaying connection status (connecting or reconnecting)
 */
export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  status,
  reconnectAttempts,
  onGoHome,
  errorMessage,
}) => {
  const { t } = useTranslation();

  const headerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    padding: '1rem',
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: '2px solid var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.6rem 1.2rem',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  };

  const handleMouseOver = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = 'scale(1.05)';
    e.currentTarget.style.opacity = '0.9';
  };

  const handleMouseOut = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.opacity = '1';
  };

  return (
    <div className="app">
      <div style={headerStyle}>
        <button
          onClick={onGoHome}
          style={buttonStyle}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          <AppIcon name="home" fontSize="small" /> {t('common.home')}
        </button>
        <div style={{ flex: 1 }}></div>
      </div>
      <div className="container" style={{ textAlign: 'center', padding: '3rem', marginTop: '80px' }}>
        {status === 'error' ? (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><AppIcon name="warning" sx={{ fontSize: '3rem' }} /></div>
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#EF4444' }}>
              {t('common.connection.connectionError')}
            </div>
            <p style={{ fontSize: '1.2rem', opacity: 0.8, marginBottom: '1rem' }}>
              {errorMessage || t('common.connection.defaultError')}
            </p>
            <p style={{ fontSize: '1rem', opacity: 0.6, marginTop: '1rem' }}>
              {t('common.connection.serverUnreachable')}
            </p>
            <button
              onClick={onGoHome}
              style={{
                marginTop: '2rem',
                padding: '0.8rem 2rem',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
              }}
            >
              {t('common.goHome')}
            </button>
          </>
        ) : (
          <>
            <div className="loading" style={{ fontSize: '2rem', marginBottom: '1rem' }}>
              {status === 'reconnecting' ? <><AppIcon name="refresh" fontSize="small" /> {t('common.connection.reconnecting')}</> : t('common.connection.connecting')}
            </div>
            {status === 'reconnecting' && reconnectAttempts !== undefined && (
              <>
                <p style={{ fontSize: '1.2rem', opacity: 0.8 }}>
                  {t('common.connection.attempt', { current: reconnectAttempts, max: 5 })}
                </p>
                <p style={{ fontSize: '1rem', opacity: 0.6, marginTop: '1rem' }}>
                  {t('common.connection.reconnectHint')}
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
